/**
 * @fileoverview
 * This is the component for the charger advanced controls form.
 *
 * Props: onSubmitSuccess - function to call when the form is submitted successfully
 *
 * Child elements: ControlLabel.js, Input.js, Select.js, Button.js, Separator.js
 *
 * Parent element: ChargerAdvancedControls.js
 */

import React, { useState, useEffect, useRef } from "react";
import useProForma from "store/useProForma";
import { Input } from "components//ui/input";
import useAuthStore from "store/useAuthStore";
import { NumericFormat } from "react-number-format";
import { updateChargerData, fetchChargerData } from "api/chargercostapi";
import ControlLabel from "components/advanced-controls/ControlLabel";

const ChargerAdvancedControlsForm = () => {
  const {
    chargerCost: chargerCosts,
    cityInfo,
    fetchAndUpdateFleet,
  } = useProForma(); // Assuming this fetches the charger data for the current user
  const [cityFormData, setCityFormData] = useState({
    range_1: "",
    range_2: "",
    range_3: "",
    range_4: "",
    port_count_1: null,
    port_count_2: null,
    port_count_3: null,
    port_count_4: null,
  });
  const [formData, setFormData] = useState({
    cost_less_than_10_kw: null,
    cost_10_20_kw: null,
    cost_25_kw: null,
    cost_180_200_kw: null,
    install_less_than_10_kw: null,
    install_10_20_kw: null,
    install_25_kw: null,
    install_180_200_kw: null,
    contingency: 0,
  });

  const { user } = useAuthStore();
  const initialChargerRef = useRef(formData);
  const initialCityRef = useRef(cityFormData);

  // Updates the form data when the chargerCosts loads
  useEffect(() => {
    const loadChargerData = async () => {
      if (!cityInfo || !user) return;

      let chargerDataToUse = null;

      // First, try to fetch from "charger costs" table
      const chargerCostsResponse = await fetchChargerData(user.id);

      if (chargerCostsResponse.data) {
        // Use data from "charger costs" table
        chargerDataToUse = chargerCostsResponse.data;
      } else if (chargerCosts) {
        // Fall back to "default data" table's charger_costs column
        chargerDataToUse = chargerCosts;
      }

      if (chargerDataToUse) {
        setFormData({
          cost_less_than_10_kw: chargerDataToUse.cost_less_than_10_kw ?? null,
          cost_10_20_kw: chargerDataToUse.cost_10_20_kw ?? null,
          cost_25_kw: chargerDataToUse.cost_25_kw ?? null,
          cost_180_200_kw: chargerDataToUse.cost_180_200_kw ?? null,
          install_less_than_10_kw:
            chargerDataToUse.install_less_than_10_kw ?? null,
          install_10_20_kw: chargerDataToUse.install_10_20_kw ?? null,
          install_25_kw: chargerDataToUse.install_25_kw ?? null,
          install_180_200_kw: chargerDataToUse.install_180_200_kw ?? null,
          contingency: chargerDataToUse.contingency ?? 0,
        });

        const nextCharger = {
          cost_less_than_10_kw: chargerDataToUse.cost_less_than_10_kw ?? null,
          cost_10_20_kw: chargerDataToUse.cost_10_20_kw ?? null,
          cost_25_kw: chargerDataToUse.cost_25_kw ?? null,
          cost_180_200_kw: chargerDataToUse.cost_180_200_kw ?? null,
          install_less_than_10_kw:
            chargerDataToUse.install_less_than_10_kw ?? null,
          install_10_20_kw: chargerDataToUse.install_10_20_kw ?? null,
          install_25_kw: chargerDataToUse.install_25_kw ?? null,
          install_180_200_kw: chargerDataToUse.install_180_200_kw ?? null,
          contingency: chargerDataToUse.contingency ?? 0,
        };
        initialChargerRef.current = nextCharger;
      }

      // Set city form data
      setCityFormData({
        range_1: cityInfo.range_1 ?? "",
        range_2: cityInfo.range_2 ?? "",
        range_3: cityInfo.range_3 ?? "",
        range_4: cityInfo.range_4 ?? "",
        port_count_1: cityInfo.port_count_1 ?? null,
        port_count_2: cityInfo.port_count_2 ?? null,
        port_count_3: cityInfo.port_count_3 ?? null,
        port_count_4: cityInfo.port_count_4 ?? null,
      });

      const nextCity = {
        range_1: cityInfo.range_1 ?? "",
        range_2: cityInfo.range_2 ?? "",
        range_3: cityInfo.range_3 ?? "",
        range_4: cityInfo.range_4 ?? "",
        port_count_1: cityInfo.port_count_1 ?? null,
        port_count_2: cityInfo.port_count_2 ?? null,
        port_count_3: cityInfo.port_count_3 ?? null,
        port_count_4: cityInfo.port_count_4 ?? null,
      };
      initialCityRef.current = nextCity;
    };

    loadChargerData();
  }, [chargerCosts, cityInfo, user]);

  // Handles numeric input changes from NumericFormat
  const handleNumericFieldChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value === "" || value === undefined ? null : Number(value),
    }));
  };

  const submitChargerData = async () => {
    try {
      const userId = cityInfo.id; // Assuming userId is available in cityInfo
      const response = await updateChargerData(userId, formData);

      if (response.error) {
        throw new Error("Failed to update data");
      }

      fetchAndUpdateFleet();
      initialChargerRef.current = formData;

    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  const hasChargerChanges = () =>
    Object.keys(formData).some(
      (key) => formData[key] !== initialChargerRef.current[key]
    );

  const handleNumericBlur = () => {
    if (!cityInfo || !user) return;
    if (!hasChargerChanges()) return;
    submitChargerData();
  };

  return (
    <form>
      <div className="space-y-4">
        <div className="px-4">
          <div className="flex items-center justify-between w-full">
            <div className="w-1/2">
              <ControlLabel
                text="Contingency"
                info=""
              />
            </div>
            <NumericFormat
              customInput={Input}
              className="w-1/3"
              name="contingency"
              value={formData.contingency || ""}
              onValueChange={(values) =>
                handleNumericFieldChange("contingency", values.value)
              }
              onBlur={handleNumericBlur}
              suffix="%"
              thousandSeparator={true}
              decimalScale={0}
              allowNegative={false}
              placeholder="0%"
            />
          </div>
        </div>
        <div className="h-[0px] border-[0.5px] border-[#cacdc9]"></div>
        <div className="px-4 space-y-4">
          <div>
            <ControlLabel
              text={
                <span className="text-[#7e817d] text-[11px] font-medium font-['Roboto'] leading-[13.20px]">
                  Charger Equipment Costs Per Station
                </span>
              }
              info="The upfront price of charging equipment, including the charger unit, mounting hardware, and cables/connectors."
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Equipment Cost per Dual Port Charger ${
                    cityInfo?.range_1 || "< 10 kW"
                  }`}
                  info=""
                />
              </div>

              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="cost_less_than_10_kw"
                value={formData.cost_less_than_10_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_less_than_10_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Equipment Cost per Dual Port Charger ${
                    cityInfo?.range_2 || "10-20 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="cost_10_20_kw"
                value={formData.cost_10_20_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_10_20_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Equipment Cost per Dual Port Charger ${
                    cityInfo?.range_3 || "40 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="cost_25_kw"
                value={formData.cost_25_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_25_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Equipment Cost per Dual Port Charger ${
                    cityInfo?.range_4 || "180-200 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="cost_180_200_kw"
                value={formData.cost_180_200_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("cost_180_200_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
          </div>
        </div>

        <div className="h-[0px] border-[0.5px] border-[#cacdc9]"></div>

        {/* Installation Costs */}
        <div className="px-4 space-y-4">
          <div>
            <ControlLabel
              text={
                <span className="text-[#7e817d] text-[11px] font-medium font-['Roboto'] leading-[13.20px]">
                  Charger Installation Costs Per Station
                </span>
              }
              info="The cost to place the charger into service at a site, including electrical work, trenching, conduit installation, panel or transformer upgrades, and associated labor."
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Install Cost per Dual Port Charger ${
                    cityInfo?.range_1 || "< 10 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="install_less_than_10_kw"
                value={formData.install_less_than_10_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange(
                    "install_less_than_10_kw",
                    values.value
                  )
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Install Cost per Dual Port Charger ${
                    cityInfo?.range_2 || "10-20 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="install_10_20_kw"
                value={formData.install_10_20_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_10_20_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Install Cost per Dual Port Charger ${
                    cityInfo?.range_3 || "40 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="install_25_kw"
                value={formData.install_25_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_25_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="w-1/2">
                <ControlLabel
                  text={`Install Cost per Dual Port Charger ${
                    cityInfo?.range_4 || "180-200 kW"
                  }`}
                  info=""
                />
              </div>
              <NumericFormat
                customInput={Input}
                className="w-1/3"
                name="install_180_200_kw"
                value={formData.install_180_200_kw || ""}
                onValueChange={(values) =>
                  handleNumericFieldChange("install_180_200_kw", values.value)
                }
                onBlur={handleNumericBlur}
                prefix="$"
                thousandSeparator={true}
                decimalScale={0}
                allowNegative={false}
              />
            </div>
          </div>
        </div>

      </div>
    </form>
  );
};

export default ChargerAdvancedControlsForm;
